import { SectionCard } from "@/components/common/SectionCard";
import { ManufacturingPeriodCloseCard } from "./ManufacturingPeriodCloseCard";
import { OverheadConsumptionStrip } from "./OverheadConsumptionStrip";
import { OverheadPlantPeriodFields } from "./OverheadPlantPeriodFields";
import { OverheadPeriodValueList } from "./OverheadPeriodValueList";
import type { useManufacturingOverheadPeriodValues } from "./useManufacturingOverheadPeriodValues";

type PeriodValuesState = ReturnType<typeof useManufacturingOverheadPeriodValues>;

function formatRate(rate: number | null): string {
  if (rate === null) return "—";
  return `${(rate * 100).toFixed(2)}%`;
}

export function ManufacturingOverheadPeriodValuesCard({ state }: { state: PeriodValuesState }) {
  const { plantId, summary, listBusy } = state;

  return (
    <SectionCard
      title="Period values"
      description="Per-plant bucket assumed and actual USD. Folders total the buckets under them. Assumed edits require an audit reason; actuals lock when close starts."
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <OverheadPlantPeriodFields state={state} />
        {summary ? (
          <div className="text-sm text-gray-600 dark:text-zinc-400">
            State: <span className="font-medium">{summary.period_state}</span>
            {" · "}
            Assumed total: ${summary.totals.assumed_total_usd.toLocaleString()}
            {" · "}
            Actual total: ${summary.totals.actual_total_usd.toLocaleString()}
          </div>
        ) : null}
      </div>

      {summary ? (
        <div className="mb-4 grid gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/25 dark:text-amber-100">
          <div>
            Current-period rate estimate: {formatRate(summary.current_rate_estimate.overhead_rate)}{" "}
            <span className="text-amber-800/80 dark:text-amber-200/80">({summary.current_rate_estimate.note})</span>
          </div>
          <div>
            Snapshot rate (prior closed): {formatRate(summary.snapshot_rate_from_prior_closed.overhead_rate)}
            {summary.snapshot_rate_from_prior_closed.prior_closed_period_start
              ? ` · from ${summary.snapshot_rate_from_prior_closed.prior_closed_period_start}`
              : ""}
            {" · "}
            <span className="text-amber-800/80 dark:text-amber-200/80">
              {summary.snapshot_rate_from_prior_closed.note}
            </span>
          </div>
        </div>
      ) : null}

      <OverheadConsumptionStrip state={state} />

      {!plantId ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Select a plant to load period values.</div>
      ) : listBusy ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading period values…</div>
      ) : !summary || summary.rows.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">No active overhead buckets yet.</div>
      ) : (
        <OverheadPeriodValueList state={state} />
      )}
      <ManufacturingPeriodCloseCard state={state} />
    </SectionCard>
  );
}
